import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { C } from '../theme/palette';

/**
 * Demande une saisie à l'utilisateur.
 *
 * `window.prompt` ne convient plus : Chrome le bloque dans un cadre, et une
 * application installée sur l'écran d'accueil d'un iPhone ne l'affiche pas du
 * tout. L'artisan touchait « Nouveau corps de métier », rien ne se passait, et
 * il n'y avait rien à en conclure.
 *
 * Le remplacement garde le contrat de `prompt` — le texte saisi, ou `null` si
 * l'on renonce — de sorte que chaque appel se lit comme avant.
 */
const ContexteDialogue = createContext(() => Promise.resolve(null));

/**
 * Donne accès à la demande de saisie.
 *
 * @returns {(message: string, defaut?: string) => Promise<string|null>} la demande
 */
export function useDemande() {
  return useContext(ContexteDialogue);
}

/**
 * Porte la boîte de saisie pour toute l'application.
 *
 * @param {object} props propriétés du composant
 * @param {import('react').ReactNode} props.children l'application
 * @returns {import('react').ReactElement} les enfants et la boîte
 */
export function DialogueProvider({ children }) {
  const [etat, setEtat] = useState(null);
  // La promesse en attente vit hors de l'etat : la replacer a chaque frappe
  // rendrait `demande` instable et reconstruirait l'arbre sous lui.
  const reponse = useRef(null);

  const demande = useCallback((message, defaut) => {
    return new Promise((resout) => {
      reponse.current = resout;
      setEtat({ message: message || '', valeur: defaut == null ? '' : String(defaut) });
    });
  }, []);

  const termine = (valeur) => {
    const resout = reponse.current;
    reponse.current = null;
    setEtat(null);
    if (resout) {
      resout(valeur);
    }
  };

  const contexte = useMemo(() => demande, [demande]);

  return (
    <ContexteDialogue.Provider value={contexte}>
      {children}
      {etat && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={etat.message}
          onClick={() => termine(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,.62)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: 18,
              width: '100%',
              maxWidth: 400,
              boxShadow: '0 20px 60px rgba(0,0,0,.4)',
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                color: C.ink,
                marginBottom: 11,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
            >
              {etat.message}
            </div>
            <input
              autoFocus
              aria-label="Ta réponse"
              value={etat.valeur}
              onChange={(e) => setEtat((p) => ({ ...p, valeur: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  termine(e.currentTarget.value);
                }
                if (e.key === 'Escape') {
                  termine(null);
                }
              }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: 12,
                fontSize: 15,
                marginBottom: 14,
              }}
            />
            <div style={{ display: 'flex', gap: 9 }}>
              <button
                onClick={() => termine(null)}
                style={{
                  flex: 1,
                  padding: 12,
                  background: C.navyXL,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 800,
                  color: C.grey,
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => termine(etat.valeur)}
                style={{
                  flex: 2,
                  padding: 12,
                  background: C.navy,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 800,
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </ContexteDialogue.Provider>
  );
}
